"use client";

import type { PlaceResult } from "@ishvaram/ui/hooks";
import { hasDevanagari } from "@/lib/place-devanagari";
import PlaceAutocomplete from "./PlaceAutocomplete";
import { PlaceChip } from "./PlaceChip";

interface BirthPlaceLabels {
  birthPlace: string;
  changePlace: string;
  pickCityHint: string;
  placePlaceholder?: string;
  placeNoMatch: string;
  placeNoMatchDevanagari: string;
}

/** The line under a typed, unpicked city: the pick hint, or a no-match way forward. */
function unresolvedHint(labels: BirthPlaceLabels, birthPlace: string, noMatch: boolean): string {
  if (!noMatch) return labels.pickCityHint;
  return hasDevanagari(birthPlace) ? labels.placeNoMatchDevanagari : labels.placeNoMatch;
}

export function BirthPlaceField({
  id,
  labels,
  birthPlace,
  selectedPlace,
  setBirthPlace,
  setSelectedPlace,
  placeIsUnresolved,
  trackStart,
  error,
  errorKey,
  noMatch = false,
  onNoMatchChange,
}: {
  id: string;
  labels: BirthPlaceLabels;
  birthPlace: string;
  selectedPlace: PlaceResult | null;
  setBirthPlace: (value: string) => void;
  setSelectedPlace: (place: PlaceResult | null) => void;
  placeIsUnresolved: boolean;
  trackStart: () => void;
  /** The blocked-submit message for this field, shown under it. */
  error?: string;
  /** Changes on every blocked submit, so the alert re-mounts and is re-announced. */
  errorKey?: number;
  /** PlaceAutocomplete's settled "this query has no rows at all" signal. */
  noMatch?: boolean;
  onNoMatchChange?: (noMatch: boolean) => void;
}) {
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const showHint = placeIsUnresolved && !error;
  const describedBy = [showHint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ");
  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-body-small font-semibold text-text-primary">
        {labels.birthPlace}
      </label>
      {selectedPlace ? (
        <PlaceChip
          place={selectedPlace}
          changeLabel={labels.changePlace}
          onChange={() => {
            trackStart();
            setSelectedPlace(null);
          }}
        />
      ) : (
        <PlaceAutocomplete
          id={id}
          value={birthPlace}
          placeholder={labels.placePlaceholder}
          invalid={Boolean(error)}
          describedBy={describedBy || undefined}
          onNoMatchChange={onNoMatchChange}
          onChange={(value) => {
            trackStart();
            setBirthPlace(value);
            setSelectedPlace(null);
          }}
          onSelect={(place) => {
            trackStart();
            setBirthPlace(place.display);
            setSelectedPlace(place);
          }}
        />
      )}
      {showHint && (
        // The no-match line lands after the search settles, outside any input
        // window, and wraps to a different line count than the pick hint. All
        // variants sit invisibly in one grid cell, so the swap never moves the
        // submit button (rule 58; same reserve as KundaliFormCard's hint).
        <div className="mt-1.5 grid text-caption text-text-muted">
          {[labels.pickCityHint, labels.placeNoMatch, labels.placeNoMatchDevanagari].map(
            (variant) => (
              <span key={variant} aria-hidden="true" className="invisible col-start-1 row-start-1">
                {variant}
              </span>
            )
          )}
          <p id={hintId} aria-live="polite" className="col-start-1 row-start-1">
            {unresolvedHint(labels, birthPlace, noMatch)}
          </p>
        </div>
      )}
      {error && (
        <p
          key={errorKey}
          id={errorId}
          role="alert"
          className="mt-1.5 text-caption text-intent-error"
        >
          {error}
        </p>
      )}
    </div>
  );
}
